/**
 * Confete da comemoração, portado de docs/prototipo/index.html (`confetti()`).
 * Mesmos números: 320 partículas, gravidade .55, atrito .985, 4,2s de queda e
 * reposição até 8s. Respeita `prefers-reduced-motion`.
 */

const COLORS = ['#ff4d5e', '#3fa9ff', '#2fd27a', '#ffb020', '#d65bff', '#ffc83d', '#ffffff'];

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  rotation: number;
  spin: number;
  color: string;
  round: boolean;
}

export function runConfetti(canvas: HTMLCanvasElement, stillVisible: () => boolean): () => void {
  const context = canvas.getContext('2d');
  if (!context) return () => undefined;

  const dpr = Math.min(2, window.devicePixelRatio || 1);
  const width = (canvas.width = window.innerWidth * dpr);
  const height = (canvas.height = window.innerHeight * dpr);

  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const particles: Particle[] = Array.from({ length: reduce ? 60 : 320 }, () => ({
    x: width / 2 + (Math.random() - 0.5) * width * 0.3,
    y: height * 0.55,
    vx: (Math.random() - 0.5) * 26 * dpr,
    vy: -(Math.random() * 26 + 10) * dpr,
    size: (Math.random() * 8 + 5) * dpr,
    rotation: Math.random() * 6,
    spin: (Math.random() - 0.5) * 0.4,
    color: COLORS[Math.floor(Math.random() * COLORS.length)]!,
    round: Math.random() < 0.3,
  }));

  const startedAt = performance.now();
  let frame = 0;

  const tick = (now: number) => {
    context.clearRect(0, 0, width, height);

    for (const particle of particles) {
      particle.vy += 0.55 * dpr;
      particle.vx *= 0.985;
      particle.vy *= 0.985;
      particle.x += particle.vx;
      particle.y += particle.vy;
      particle.rotation += particle.spin;

      context.save();
      context.translate(particle.x, particle.y);
      context.rotate(particle.rotation);
      context.fillStyle = particle.color;

      if (particle.round) {
        context.beginPath();
        context.arc(0, 0, particle.size / 2, 0, 7);
        context.fill();
      } else {
        context.fillRect(
          -particle.size / 2,
          -particle.size / 4,
          particle.size,
          (particle.size / 2) * Math.abs(Math.cos(particle.rotation * 2)) + 1,
        );
      }

      context.restore();
    }

    const elapsed = now - startedAt;

    if (elapsed < 4200 && !reduce) {
      frame = requestAnimationFrame(tick);
    } else if (elapsed < 8000 && stillVisible() && !reduce) {
      // Repõe o que saiu da tela, para a chuva continuar enquanto a
      // comemoração está no ar.
      for (const particle of particles) {
        if (particle.y > height) {
          particle.x = Math.random() * width;
          particle.y = -20;
          particle.vy = Math.random() * 4;
          particle.vx = (Math.random() - 0.5) * 4;
        }
      }
      frame = requestAnimationFrame(tick);
    }
  };

  frame = requestAnimationFrame(tick);

  return () => cancelAnimationFrame(frame);
}
